import { Menu } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { MarketingCta } from "./marketing-cta";

type MarketingLayoutProps = {
  children: ReactNode;
};

type NavLink = {
  to: string;
  label: string;
};

const NAV_LINKS: NavLink[] = [
  { to: "/about", label: "About" },
  { to: "/features", label: "Features" },
  { to: "/coming-soon", label: "Coming Soon" },
  { to: "/contact", label: "Contact" },
];

export function MarketingLayout({ children }: MarketingLayoutProps) {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <header className="sticky top-0 z-40 border-border border-b bg-background">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Sheet onOpenChange={setMobileNavOpen} open={mobileNavOpen}>
              <SheetTrigger asChild>
                <Button
                  aria-label="Open navigation menu"
                  className="md:hidden"
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <Menu aria-hidden="true" className="h-5 w-5" />
                </Button>
              </SheetTrigger>
              <SheetContent className="flex flex-col gap-6" side="left">
                <SheetTitle className="sr-only">Navigation menu</SheetTitle>
                <span className="font-bold font-display text-lg">Verderem</span>
                <nav className="flex flex-col gap-4">
                  {NAV_LINKS.map((link) => (
                    <Link
                      className="text-base"
                      key={link.to}
                      onClick={() => setMobileNavOpen(false)}
                      to={link.to}
                    >
                      {link.label}
                    </Link>
                  ))}
                </nav>
                <MarketingCta />
              </SheetContent>
            </Sheet>
            <Link className="font-bold font-display text-lg" to="/">
              Verderem
            </Link>
          </div>
          <nav className="hidden items-center gap-6 md:flex">
            {NAV_LINKS.map((link) => (
              <Link
                className="text-muted-foreground text-sm hover:text-foreground"
                key={link.to}
                to={link.to}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center">
            <MarketingCta />
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-border border-t">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-8 text-center md:px-6">
          <span className="font-bold font-display text-lg">Verderem</span>
          <nav className="flex flex-wrap items-center justify-center gap-4">
            {NAV_LINKS.map((link) => (
              <Link
                className="text-muted-foreground text-sm hover:text-foreground"
                key={link.to}
                to={link.to}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <p className="text-muted-foreground text-xs">
            © {new Date().getFullYear()} Verderem
          </p>
        </div>
      </footer>
    </div>
  );
}
